'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from './cn';
import type { WhatsAppWidgetProps, WhatsAppWidgetAgent } from './WhatsAppWidget.types';

function buildWhatsAppUrl(phoneNumber: string, message?: string): string {
  const sanitised = phoneNumber.replace(/\D/g, '');
  const base = `https://wa.me/${sanitised}`;
  if (message) {
    return `${base}?text=${encodeURIComponent(message)}`;
  }
  return base;
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function AgentAvatar({ agent }: { agent: WhatsAppWidgetAgent }) {
  if (agent.avatar) {
    return (
      <img
        src={agent.avatar}
        alt={agent.name}
        className="w-10 h-10 rounded-full object-cover object-top"
      />
    );
  }

  const initials = agent.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center text-sm font-semibold text-[#25D366]">
      {initials}
    </div>
  );
}

function AgentCard({
  agent,
  defaultMessage,
  onClick,
}: {
  agent: WhatsAppWidgetAgent;
  defaultMessage?: string;
  onClick?: (phoneNumber: string, agentName: string) => void;
}) {
  const url = buildWhatsAppUrl(agent.phoneNumber, defaultMessage);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onClick?.(agent.phoneNumber, agent.name)}
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
    >
      <AgentAvatar agent={agent} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{agent.name}</p>
        {agent.department && (
          <p className="text-xs text-gray-500 truncate">{agent.department}</p>
        )}
        {agent.availabilityText && (
          <p className="text-xs text-gray-400 truncate mt-0.5">{agent.availabilityText}</p>
        )}
      </div>
      <div className="shrink-0 w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <WhatsAppIcon className="w-4 h-4 text-white" />
      </div>
    </a>
  );
}

export function WhatsAppWidget({
  agents,
  defaultMessage,
  position = 'bottom-right',
  onClickAgent,
}: WhatsAppWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const isSingleAgent = agents.length === 1;

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleClose();
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  if (!agents.length) return null;

  const positionClasses =
    position === 'bottom-left' ? 'left-5' : 'right-5';

  const popupPositionClasses =
    position === 'bottom-left' ? 'left-0' : 'right-0';

  // Single agent: the FAB is a direct link to WhatsApp
  if (isSingleAgent) {
    const agent = agents[0]!;
    const url = buildWhatsAppUrl(agent.phoneNumber, defaultMessage);

    return (
      <div
        className={cn('fixed bottom-5 z-9990', positionClasses)}
      >
        <a
          ref={buttonRef as React.RefObject<HTMLAnchorElement>}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onClickAgent?.(agent.phoneNumber, agent.name)}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2"
          aria-label={`Chat with ${agent.name} on WhatsApp`}
        >
          <WhatsAppIcon className="w-7 h-7" />
        </a>
      </div>
    );
  }

  // Multi-agent: FAB opens a popup with agent selection
  return (
    <div
      className={cn('fixed bottom-5 z-9990', positionClasses)}
    >
      {/* Agent selection popup */}
      <div
        ref={popupRef}
        className={cn(
          'absolute bottom-[calc(100%+0.75rem)] w-80 max-w-[calc(100vw-2.5rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-200 origin-bottom',
          popupPositionClasses,
          isOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none',
        )}
        role="dialog"
        aria-label="Choose a WhatsApp contact"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="bg-[#25D366] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WhatsAppIcon className="w-5 h-5 text-white" />
            <span className="text-white font-semibold text-sm">Chat with us</span>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
            aria-label="Close"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Agent list */}
        <div className="p-2 max-h-72 overflow-y-auto">
          <p className="px-3 py-2 text-xs text-gray-400 font-medium">
            Select a team member to start chatting
          </p>
          {agents.map((agent) => (
            <AgentCard
              key={agent.phoneNumber}
              agent={agent}
              {...(defaultMessage != null ? { defaultMessage } : {})}
              onClick={(phone, name) => {
                onClickAgent?.(phone, name);
                handleClose();
              }}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-100">
          <p className="text-[10px] text-gray-300 text-center">
            Powered by WhatsApp
          </p>
        </div>
      </div>

      {/* FAB button */}
      <button
        ref={buttonRef as React.RefObject<HTMLButtonElement>}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex items-center justify-center w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2',
          isOpen
            ? 'bg-gray-700 hover:bg-gray-800 scale-95'
            : 'bg-[#25D366] hover:scale-105',
        )}
        aria-label={isOpen ? 'Close WhatsApp chat menu' : 'Open WhatsApp chat menu'}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <div
          className={cn(
            'transition-transform duration-200 text-white',
            isOpen ? 'rotate-90' : 'rotate-0',
          )}
        >
          {isOpen ? (
            <CloseIcon className="w-6 h-6" />
          ) : (
            <WhatsAppIcon className="w-7 h-7" />
          )}
        </div>
      </button>
    </div>
  );
}
